import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InvoiceLine {
  id: number;
  lineType: 'time_entry' | 'expense' | 'manual';
  timeEntryId: number | null;
  expenseId: number | null;
  description: string;
  quantity: number;
  unitRate: number;
  lineTotal: number;
  billable: boolean;
  createdAt: string;
}

export interface InvoicePayment {
  id: number;
  paymentDate: string;
  amount: number;
  paymentMethod: string | null;
  reference: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
}

export interface InvoiceAdjustment {
  id: number;
  adjustmentType: 'write_off' | 'discount' | 'reversal' | 'credit';
  amount: number;
  reason: string;
  approvedBy: string | null;
  createdBy: string;
  createdAt: string;
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  caseRef: string;
  clientName: string | null;
  status: 'draft' | 'sent' | 'paid' | 'void' | 'write_off';
  currency: string;
  issuedDate: string | null;
  dueDate: string | null;
  totalAmount: number;
  amountPaid: number;
  amountAdjusted: number;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lines?: InvoiceLine[];
  payments?: InvoicePayment[];
  adjustments?: InvoiceAdjustment[];
}

export interface PrebillItem {
  id: number;
  item_type: 'time_entry' | 'expense';
  caseRef: string;
  userId: string;
  billable: boolean;
  // time entry fields
  startedAt?: string;
  endedAt?: string;
  durationSeconds?: number;
  units?: number;
  taskDescription?: string;
  rateApplied?: number;
  activityCodeId?: number | null;
  activityCode?: string | null;
  // expense fields
  expenseDate?: string;
  description?: string;
  amount?: number;
  currency?: string;
}

export interface PrebillQueue {
  timeEntries: PrebillItem[];
  expenses: PrebillItem[];
}

export interface CreateInvoiceInput {
  caseRef: string;
  clientName?: string;
  currency?: string;
  issuedDate?: string;
  dueDate?: string;
  notes?: string;
  items?: { type: 'time_entry' | 'expense'; id: number }[];
}

export interface AddLineInput {
  type: 'time_entry' | 'expense' | 'manual';
  id?: number;
  description?: string;
  quantity?: number;
  unitRate?: number;
  billable?: boolean;
}

export interface AddPaymentInput {
  paymentDate: string;
  amount: number;
  paymentMethod?: string;
  reference?: string;
  notes?: string;
}

export interface AddAdjustmentInput {
  adjustmentType: 'write_off' | 'discount' | 'reversal' | 'credit';
  amount: number;
  reason: string;
  approvedBy?: string;
}

export interface ARReportItem {
  id: number;
  invoiceNumber: string;
  caseRef: string;
  clientName: string | null;
  status: string;
  issuedDate: string | null;
  dueDate: string | null;
  totalAmount: number;
  amountPaid: number;
  amountAdjusted: number;
  outstandingAmount: number;
  ageBucket: 'current' | '1-30' | '31-60' | '61-90' | '90+';
}

export interface ARReport {
  items: ARReportItem[];
  summary: Record<string, { count: number; outstanding: number }>;
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const INVOICES_KEY = 'invoices';
const invoiceKey = (id: number) => ['invoice', id] as const;
const prebillKey = (caseRef?: string) => ['prebill-queue', caseRef ?? null] as const;
const arKey = () => ['ar-report'] as const;

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useInvoices(params: { caseRef?: string; status?: string } = {}) {
  const { caseRef, status } = params;
  return useQuery({
    queryKey: [INVOICES_KEY, { caseRef, status }],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (caseRef) qs.set('caseRef', caseRef);
      if (status) qs.set('status', status);
      const res = await api.get<{ success: boolean; items: Invoice[] }>(
        `/api/invoices?${qs.toString()}`,
      );
      return res.data.items ?? [];
    },
    staleTime: 30_000,
  });
}

export function useInvoice(id: number | null) {
  return useQuery({
    queryKey: invoiceKey(id!),
    queryFn: async () => {
      const res = await api.get<{ success: boolean; item: Invoice }>(`/api/invoices/${id}`);
      return res.data.item;
    },
    enabled: id !== null && id > 0,
    staleTime: 30_000,
  });
}

export function usePrebillQueue(caseRef?: string) {
  return useQuery({
    queryKey: prebillKey(caseRef),
    queryFn: async () => {
      const qs = caseRef ? `?caseRef=${encodeURIComponent(caseRef)}` : '';
      const res = await api.get<PrebillQueue>(`/api/invoices/prebill-queue${qs}`);
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useARReport(caseRef?: string) {
  return useQuery({
    queryKey: [...arKey(), caseRef ?? null],
    queryFn: async () => {
      const qs = caseRef ? `?caseRef=${encodeURIComponent(caseRef)}` : '';
      const res = await api.get<ARReport>(`/api/invoices/ar-report${qs}`);
      return res.data;
    },
    staleTime: 60_000,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInvoiceInput) =>
      api.post<{ success: boolean; item: Invoice }>('/api/invoices', data).then((r) => r.data.item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
      qc.invalidateQueries({ queryKey: prebillKey() });
    },
  });
}

export function useUpdateInvoiceStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api
        .patch<{ success: boolean; item: Invoice }>(`/api/invoices/${id}/status`, { status })
        .then((r) => r.data.item),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
      qc.invalidateQueries({ queryKey: invoiceKey(id) });
      qc.invalidateQueries({ queryKey: arKey() });
    },
  });
}

export function useAddInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, line }: { id: number; line: AddLineInput }) =>
      api
        .post<{ success: boolean; item: Invoice }>(`/api/invoices/${id}/lines`, line)
        .then((r) => r.data.item),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: invoiceKey(id) });
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
    },
  });
}

export function useRemoveInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, lineId }: { invoiceId: number; lineId: number }) =>
      api
        .delete<{ success: boolean; item: Invoice }>(`/api/invoices/${invoiceId}/lines/${lineId}`)
        .then((r) => r.data.item),
    onSuccess: (_, { invoiceId }) => {
      qc.invalidateQueries({ queryKey: invoiceKey(invoiceId) });
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
    },
  });
}

export function useAddPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payment }: { id: number; payment: AddPaymentInput }) =>
      api
        .post<{ success: boolean; item: Invoice }>(`/api/invoices/${id}/payments`, payment)
        .then((r) => r.data.item),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: invoiceKey(id) });
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
      qc.invalidateQueries({ queryKey: arKey() });
    },
  });
}

export function useAddAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, adjustment }: { id: number; adjustment: AddAdjustmentInput }) =>
      api
        .post<{ success: boolean; item: Invoice }>(`/api/invoices/${id}/adjustments`, adjustment)
        .then((r) => r.data.item),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: invoiceKey(id) });
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
      qc.invalidateQueries({ queryKey: arKey() });
    },
  });
}
